import { useState } from "react";
import { isAxiosError } from "axios";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight, Folder, LoaderCircle } from "lucide-react";
import { AppModal, Button, Input } from "@/components/ui";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { searchMattersInfiniteQueryOptions } from "@/lib/data/react-query/matters/search-matters";
import {
  retrieveFileOptions,
  useUpdateCanonicalFile,
} from "@/lib/data/react-query/files/canonical-files";
import { mailFilingFoldersOptions } from "@/lib/data/react-query/mail-intake";
import { fileOperationMessage } from "@/lib/data/requests/files/canonical-files";
import { MATTER_STATUS_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import type { MailIntakeItem } from "@/lib/data/schemas/mail-intake";

export function MailFilingDialog({
  item,
  onClose,
}: {
  item: MailIntakeItem;
  onClose: () => void;
}) {
  const [matter, setMatter] = useState<{ id: string; title: string }>();
  const [path, setPath] = useState<{ id: string; name: string }[]>([]);
  const [search, setSearch] = useState("");
  const searchQuery = useDebouncedValue(search, 250);
  const matters = useInfiniteQuery({
    ...searchMattersInfiniteQueryOptions({
      context: "files",
      statusCategories: [...MATTER_STATUS_CATEGORY_OPTIONS],
      search: searchQuery,
      sort: "title",
      order: "asc",
      pageSize: 50,
    }),
    enabled: !matter,
  });
  const folderId = path.at(-1)?.id;
  const folders = useInfiniteQuery(
    mailFilingFoldersOptions(matter?.id, folderId),
  );
  const file = useQuery(retrieveFileOptions(item.fileId ?? ""));
  const move = useUpdateCanonicalFile();
  const stale = isAxiosError(move.error) && move.error.response?.status === 409;
  const children =
    folders.data?.pages
      .flatMap((page) => page.data)
      .filter(
        (folder) => folder.isFolder && !folder.systemFolder && !folder.skillId,
      ) ?? [];
  const close = () => {
    if (!move.isPending) onClose();
  };

  return (
    <AppModal
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title={matter ? "Choose folder" : "Choose matter"}
      description="Choose a matter, then an existing folder for this document."
      size="2xl"
      className="h-[min(80dvh,720px)]"
      bodyContained
      loading={move.isPending}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={close} disabled={move.isPending}>
            Cancel
          </Button>
          {matter && (
            <Button
              disabled={
                !folderId ||
                !file.data?.capabilities.canMove ||
                file.isFetching ||
                file.isError ||
                folders.isPending ||
                folders.isError ||
                stale ||
                move.isPending
              }
              onClick={() => {
                if (!folderId || !file.data) return;
                move.mutate(
                  {
                    id: file.data.id,
                    revision: file.data.revision,
                    destination: { parentId: folderId },
                  },
                  { onSuccess: onClose },
                );
              }}
            >
              Move here
            </Button>
          )}
        </div>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 p-5">
        <div className="bg-muted/40 rounded-lg border px-3 py-2">
          <p className="truncate text-sm font-medium" title={item.name}>
            {item.name}
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            {matter
              ? "Open a folder, then choose Move here."
              : "Search for the client or matter this document belongs to."}
          </p>
        </div>
        {file.isError && (
          <p role="alert" className="text-destructive text-sm">
            {fileOperationMessage(
              file.error,
              "This file could not be loaded. Close and try again.",
            )}
          </p>
        )}
        {file.data && !file.data.capabilities.canMove && (
          <p role="alert" className="text-destructive text-sm">
            You no longer have permission to move this file.
          </p>
        )}
        {move.isError && (
          <p role="alert" className="text-destructive text-sm">
            {stale
              ? "The file or destination changed. Close and choose the destination again."
              : fileOperationMessage(
                  move.error,
                  "This file could not be moved. Try another folder.",
                )}
          </p>
        )}
        {matter ? (
          <>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  move.reset();
                  if (path.length) setPath(path.slice(0, -1));
                  else setMatter(undefined);
                }}
              >
                <ArrowLeft className="mr-2 size-4" />
                {path.length ? "Back" : "Change matter"}
              </Button>
              <p
                className="min-w-0 flex-1 truncate text-sm"
                title={[
                  matter.title,
                  ...path.map((folder) => folder.name),
                ].join(" / ")}
              >
                {[matter.title, ...path.map((folder) => folder.name)].join(
                  " / ",
                )}
              </p>
            </div>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-xl border p-3">
              {folders.isPending ? (
                <p role="status" className="text-muted-foreground p-4 text-sm">
                  Loading folders…
                </p>
              ) : folders.isError ? (
                <p role="alert" className="text-destructive p-4 text-sm">
                  {fileOperationMessage(
                    folders.error,
                    "Folders could not be loaded. Close and try again.",
                  )}
                </p>
              ) : (
                <>
                  {children.map((folder) => (
                    <Button
                      key={folder.id}
                      variant="outline"
                      className="h-auto w-full justify-start gap-3 py-3 text-left"
                      onClick={() => {
                        move.reset();
                        setPath([
                          ...path,
                          { id: folder.id, name: folder.name },
                        ]);
                      }}
                    >
                      <Folder className="size-4 shrink-0" />
                      <span className="min-w-0 flex-1 truncate">
                        {folder.name}
                      </span>
                      <ChevronRight className="size-4 shrink-0" />
                    </Button>
                  ))}
                  {!children.length && !folders.hasNextPage && (
                    <p className="text-muted-foreground p-4 text-center text-sm">
                      {folderId
                        ? "No subfolders. You can file this document here."
                        : "No folders in this matter. Choose another matter or add a folder in its Files page."}
                    </p>
                  )}
                </>
              )}
              {folders.hasNextPage && (
                <Button
                  variant="ghost"
                  className="w-full"
                  disabled={folders.isFetchingNextPage}
                  onClick={() => void folders.fetchNextPage()}
                >
                  {folders.isFetchingNextPage ? "Loading…" : "More folders"}
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            <Input
              aria-label="Search matters"
              placeholder="Search clients or matters…"
              value={search}
              maxLength={200}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-xl border p-3">
              {matters.isPending ? (
                <div
                  role="status"
                  className="text-muted-foreground flex items-center justify-center gap-2 p-8 text-sm"
                >
                  <LoaderCircle className="size-4 animate-spin" />
                  Loading matters…
                </div>
              ) : matters.isError ? (
                <p role="alert" className="text-destructive p-4 text-sm">
                  {fileOperationMessage(
                    matters.error,
                    "Matters could not be loaded. Close and try again.",
                  )}
                </p>
              ) : (
                <>
                  {matters.data.pages
                    .flatMap((page) => page.data)
                    .map((result) => (
                      <Button
                        key={result.id}
                        variant="outline"
                        className="h-auto w-full justify-between gap-3 py-3 text-left"
                        onClick={() => {
                          setMatter({
                            id: result.id,
                            title: result.title || "Untitled matter",
                          });
                          setPath([]);
                        }}
                      >
                        <span className="min-w-0 truncate">
                          {result.title || "Untitled matter"}
                        </span>
                        <ChevronRight className="size-4 shrink-0" />
                      </Button>
                    ))}
                  {!matters.data.pages.some((page) => page.data.length) && (
                    <p className="text-muted-foreground p-8 text-center text-sm">
                      No matters found.
                    </p>
                  )}
                </>
              )}
              {matters.hasNextPage && (
                <Button
                  variant="ghost"
                  className="w-full"
                  disabled={matters.isFetchingNextPage}
                  onClick={() => void matters.fetchNextPage()}
                >
                  {matters.isFetchingNextPage
                    ? "Loading…"
                    : "Load more matters"}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </AppModal>
  );
}
